import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import AppShell, { SettingsGroup } from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import { fetchNotificationSettings, updateNotificationSettings } from '../services/api';
import { useT } from '../i18n/useT';

export default function NotificationSettingsPage() {
  const t = useT();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [push, setPush] = useState(false);
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const load = useCallback(async () => {
    if (!user?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNotificationSettings(user.token, getApiLanguage());
      const s = res.settings;
      if (s) {
        setPush(!!s.push_enabled);
        setEmail(!!s.email_enabled);
        setSms(!!s.sms_enabled);
        setMarketing(!!s.marketing_enabled);
      }
    } catch (e) {
      console.error(e);
      setError(t('notificationSettingsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!user?.token) return;
    setSaving(true);
    setError(null);
    try {
      await updateNotificationSettings(
        {
          push_enabled: push,
          email_enabled: email,
          sms_enabled: sms,
          marketing_enabled: marketing,
        },
        user.token,
        getApiLanguage()
      );
    } catch (e) {
      console.error(e);
      setError(t('notificationSettingsSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell title={t('settingsNotifications')} showBackButton maxWidth="sm" showCompanion={false}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SettingsGroup>
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel control={<Switch checked={push} onChange={(e) => setPush(e.target.checked)} />} label={t('notificationPush')} />
            <FormControlLabel control={<Switch checked={email} onChange={(e) => setEmail(e.target.checked)} />} label={t('email')} />
            <FormControlLabel control={<Switch checked={sms} onChange={(e) => setSms(e.target.checked)} />} label={t('notificationSms')} />
            <FormControlLabel
              control={<Switch checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />}
              label={t('notificationMarketing')}
            />
            <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary, mt: 1 }}>
              {t('notificationMarketingHint')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={saving}
              sx={{ mt: 1, alignSelf: 'flex-start', textTransform: 'none', borderRadius: 999, boxShadow: 'none' }}
            >
              {saving ? t('saving') : t('saveSettings')}
            </Button>
          </Box>
        </SettingsGroup>
      )}
    </AppShell>
  );
}
